import { TableCell } from '@/primitives';
import {
  type EbvConfigItem,
  type EbvGroupingOptions,
} from '@/hooks/publicSearch/ebv/ebvColumns';
import { formatEbv, getEbvCellContext, getEbvValue } from './helpers';
import { STICKY_PRIMARY_ROW_BORDER, Z_PRIMARY_EBV } from './constants';
import type { MatingTableChrome } from './useMatingTableChrome';
import type { AnimalEbvRecord, MatingPairResult } from '../types';

interface RenderEbvCellsArgs {
  animal: AnimalEbvRecord;
  configs: EbvConfigItem[];
  chrome: MatingTableChrome;
  currencySymbol: string;
  groupStartFields: Set<string>;
  primaryStickyTop: number;
  ebvGroupingOptions?: EbvGroupingOptions;
  options?: { primaryRow?: boolean };
}

export function renderEbvCells({
  animal,
  configs,
  chrome,
  currencySymbol,
  groupStartFields,
  primaryStickyTop,
  ebvGroupingOptions,
  options,
}: RenderEbvCellsArgs) {
  return configs.map((ebvConfig, index) => {
    const ebvNumber = parseInt(ebvConfig.field.replace(/[^\d]/g, ''), 10);
    const ebvValue = getEbvValue(animal, ebvNumber);
    const formatted = formatEbv(ebvValue, ebvConfig.format, currencySymbol);
    const primary = options?.primaryRow === true;
    const bg = primary ? chrome.primaryRowBaseBg : 'background.paper';
    // Category-colour theming for non-index cells (matches the animal list
    // grid): subtle fill + a category-coloured block border, inner borders
    // deduped between adjacent same-category cells.
    const { highlighted, prevHighlighted, tint, previousSameCategory } =
      getEbvCellContext(ebvConfig, configs[index - 1], ebvGroupingOptions);
    return (
      <TableCell
        key={ebvConfig.field}
        align="center"
        sx={{
          whiteSpace: 'nowrap',
          fontSize: '0.8125rem',
          py: 1,
          color: 'text.primary',
          fontWeight: primary ? 700 : undefined,
          borderBottom: primary ? STICKY_PRIMARY_ROW_BORDER : chrome.cellBorder,
          borderBottomColor: primary ? chrome.primaryRowBorderColor : undefined,
          backgroundColor: bg,
          ...(groupStartFields.has(ebvConfig.field) && {
            borderLeft: chrome.cellBorder,
          }),
          ...(highlighted && {
            borderRight: chrome.ebvHighlightBorder,
            ...(prevHighlighted
              ? {}
              : { borderLeft: chrome.ebvHighlightBorder }),
          }),
          ...(!highlighted && {
            // Keep the primary (sire/dam) row's own fill; still draw the
            // category vertical borders through it for block continuity.
            ...(primary
              ? {}
              : { backgroundColor: chrome.categoryCellBg(tint) }),
            borderRight: chrome.categoryBorder(tint),
            ...(previousSameCategory
              ? {}
              : { borderLeft: chrome.categoryBorder(tint) }),
          }),
          ...(primary && {
            position: 'sticky' as const,
            top: primaryStickyTop,
            zIndex: Z_PRIMARY_EBV,
            borderRadius: 0,
            borderBottomLeftRadius: 0,
            borderBottomRightRadius: 0,
          }),
        }}
      >
        {formatted}
      </TableCell>
    );
  });
}

interface RenderProjectedEbvCellsArgs {
  result: MatingPairResult;
  configs: EbvConfigItem[];
  chrome: MatingTableChrome;
  currencySymbol: string;
  groupStartFields: Set<string>;
  ebvGroupingOptions?: EbvGroupingOptions;
}

export function renderProjectedEbvCells({
  result,
  configs,
  chrome,
  currencySymbol,
  groupStartFields,
  ebvGroupingOptions,
}: RenderProjectedEbvCellsArgs) {
  return configs.map((ebvConfig, index) => {
    const ebvNumber = parseInt(ebvConfig.field.replace(/[^\d]/g, ''), 10);
    const projected = result.projectedEbvs.find(
      (candidate) => candidate.ebvNumber === ebvNumber,
    );
    const formatted = formatEbv(
      projected?.value ?? null,
      ebvConfig.format,
      currencySymbol,
    );
    const { highlighted, prevHighlighted, tint, previousSameCategory } =
      getEbvCellContext(ebvConfig, configs[index - 1], ebvGroupingOptions);
    return (
      <TableCell
        key={ebvConfig.field}
        align="center"
        sx={{
          whiteSpace: 'nowrap',
          fontSize: '0.8125rem',
          py: 1,
          color: 'text.primary',
          backgroundColor: chrome.epaRowBg,
          ...(groupStartFields.has(ebvConfig.field) && {
            borderLeft: chrome.cellBorder,
          }),
          ...(highlighted && {
            borderRight: chrome.ebvHighlightBorder,
            ...(prevHighlighted
              ? {}
              : { borderLeft: chrome.ebvHighlightBorder }),
          }),
          // Continue the category block borders through the EPA row (keep its
          // own grey fill so the projected row stays visually distinct).
          ...(!highlighted && {
            borderRight: chrome.categoryBorder(tint),
            ...(previousSameCategory
              ? {}
              : { borderLeft: chrome.categoryBorder(tint) }),
          }),
        }}
      >
        {formatted}
      </TableCell>
    );
  });
}
